import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import config from "../config"; // Import the config file

const StudentSignup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    Name: "",
    Email: "",
    Password: "",
    Section: "Primary", // Default value
    Class: "", // Added field to match your example
  });
  const [subjects, setSubjects] = useState({}); // Store subjects by section
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Handle input changes for all fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Fetch subjects from local JSON file
  const fetchSubjects = async () => {
    try {
      const response = await fetch("/dataSubjects.json");
      if (!response.ok) throw new Error("Failed to load subjects data.");
      const subjectsData = await response.json();
      setSubjects(subjectsData); // Store subjects data by section
      console.log("Subjects data:", subjectsData); // Debugging log
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setErrors((prev) => ({ ...prev, fetch: "Failed to fetch subjects." }));
    }
  };

  // Function to generate results structure based on subjects
  const generateResults = (subjects) => {
    return subjects.map((subject) => {
      const ca1 = 0;
      const ca2 = 0;
      const clssWrk = 0;
      const project = 0;
      const exam = 0;
      const score = ca1 + + ca2 + clssWrk + project + exam; // Total Score
      const comment =
        score >= 85
          ? "Excellent Work"
          : score >= 70
          ? "Good Effort"
          : score >= 50
          ? "Needs Improvement"
          : "No Comment";

      return {
        Subject: subject,
        CA1: ca1,
        CA2: ca2,
        ClassWork: clssWrk,
        Project: project,
        Exam: exam,
        Score: score,
        Comment: comment,
        Date: new Date().toLocaleString("en-US", {
          timeZone: "UTC",
          dateStyle: "full",
          timeStyle: "long",
        }),
      };
    });
  };

  // Submit logic for signup
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate inputs
    if (!formData.Name) newErrors.Name = "Name is required.";
    if (!formData.Email) newErrors.Email = "Email is required.";
    if (!formData.Password) newErrors.Password = "Password is required.";
    if (!formData.Class) newErrors.Class = "Class is required.";
    if (!formData.Section) newErrors.Section = "Section is required.";

    // If validation fails, set errors and stop execution
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Get subjects for the student's section
    const sectionSubjects = subjects[formData.Section]?.subjects || [];
    console.log("Selected section subjects:", sectionSubjects); // Debugging log

    if (sectionSubjects.length === 0) {
      setErrors((prev) => ({
        ...prev,
        submit: "No subjects found for this section.",
      }));
      return;
    }

    // Generate results based on the subjects
    const results = generateResults(sectionSubjects);

    // Complete the form data with the generated results
    const studentData = {
      ...formData,
      Results: results,
    };

    // Proceed with signup logic
    try {
      setLoading(true);
      console.log("Sending signup data to server:", studentData);

      // Example API request to save data (adjust URL to match your JSON server)
      const response = await fetch(`${config.apiEndpoint}/${formData.Class}/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(studentData),
      });

      if (!response.ok) {
        throw new Error("Failed to register the student.");
      }

      // On successful signup, redirect to the login page
      console.log("Signup successful!");
      navigate("/student-login");
    } catch (error) {
      console.error("Signup failed:", error);
      setErrors((prev) => ({
        ...prev,
        submit: "Failed to register. Please try again later.",
      }));
    } finally {
      setLoading(false);
    }
  };

  // Set subjects based on selected section when the component mounts
  useEffect(() => {
    fetchSubjects(); // Fetch subjects from local JSON file
  }, []);


  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-r from-yellow-500 to-yellow-700 p-6">
      <form
        className="w-full max-w-md bg-white p-6 md:px-10 rounded-lg shadow-lg"
        onSubmit={handleSubmit}
      >
        <h2 className="text-2xl font-bold text-center mb-6">Student Signup</h2>

        {/* Name Input */}
        <div className="mb-4">
          <label htmlFor="Name" className="block text-gray-700 font-bold mb-2">
            Name
          </label>
          <input
            id="Name"
            name="Name"
            type="text"
            value={formData.Name}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-yellow-700"
            placeholder="Enter Your Name"
          />
          {errors.Name && (
            <p className="text-red-500 text-sm">{errors.Name}</p>
          )}
        </div>

        {/* Email Input */}
        <div className="mb-4">
          <label htmlFor="Email" className="block text-gray-700 font-bold mb-2">
            Email
          </label>
          <input
            id="Email"
            name="Email"
            type="email"
            value={formData.Email}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-yellow-700"
            placeholder="Enter Your Email"
          />
          {errors.Email && (
            <p className="text-red-500 text-sm">{errors.Email}</p>
          )}
        </div>

        {/* Password Input */}
        <div className="mb-4">
          <label
            htmlFor="Password"
            className="block text-gray-700 font-bold mb-2"
          >
            Password
          </label>
          <input
            id="Password"
            name="Password"
            type="password"
            value={formData.Password}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-yellow-700"
            placeholder="Enter Your Password"
          />
          {errors.Password && (
            <p className="text-red-500 text-sm">{errors.Password}</p>
          )}
        </div>

        {/* Class Input */}
        <div className="mb-4">
          <label htmlFor="Class" className="block text-gray-700 font-bold mb-2">
            Class
          </label>
          <input
            id="Class"
            name="Class"
            type="text"
            value={formData.Class}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-yellow-700"
            placeholder="Enter Your Class (E.g Pri 3, JS 2, SS 3 etc.)"
          />
          {errors.Class && (
            <p className="text-red-500 text-sm">{errors.Class}</p>
          )}
        </div>

        {/* Section Dropdown */}
        <div className="mb-6">
          <label
            htmlFor="Section"
            className="block text-gray-700 font-bold mb-2"
          >
            Section
          </label>
          <select
            id="Section"
            name="Section"
            value={formData.Section}
            onChange={handleChange}
            className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-yellow-700"
          >
            <option value="Nursery">Nursery</option>
            <option value="Primary">Primary</option>
            <option value="Junior-Secondary">Junior Secondary</option>
            <option value="Senior-Secondary">Senior Secondary</option>
          </select>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <p className="font-medium text-red-500 mb-4">{errors.submit}</p>
        )}

        {/* Loading State */}
        {loading && <p className="font-medium text-blue-500 mb-4">Registering...</p>}

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-yellow-500 text-white font-bold py-2 rounded-lg hover:bg-yellow-700 transition duration-300"
          disabled={loading}
        >
          Signup
        </button>
      </form>
    </div>
  );
};

export default StudentSignup;
